/**
 * Hook de orquestração para seleção de pedido
 * Centraliza:
 * - Estado do input/filtro
 * - Busca de pedidos (TanStack)
 * - Seleção de pedido
 * - Integração com store Zustand
 * - Callbacks para seleção, limpar, etc.
 * - Estados de loading/erro
 * - Conexão automática usando CONNECTOR UNIFICADO com múltiplas tentativas:
 *   1. Busca por codProdForn com quantidade exata, maior/igual ou qualquer disponível
 *   2. Busca cruzada: codProdForn XML com codProduto Pedido
 *   3. Busca parcial: códigos que começam com o código do XML (ex: XML "60001233" → Pedido "60001233-I")
 *
 * @example
 * const props = usePedidoDialogCard();
 */
"use client";

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { usePrenotaStore, OrigemProduto, Produto } from "@PN/inclusao";
import { useSearchPedidos_tanstack } from "./_tanstack";
import { connectXmlWithPedido } from "./Connector";
import { apiClient, URL } from "api";
import type { Pedido } from "@apiInclui/pedido";
import { identificacaoUtils } from "../../_utils";

// ✅ USA UTILS UNIFICADO: Gera chave única do pedido
export function getPedidoKey(pedido: Pedido): string {
  return identificacaoUtils.pedido.generateKey(
    pedido.codPedido,
    pedido.filPedido
  );
}

// ✅ FUNÇÃO UTILITÁRIA: Extrai produtos dos pedidos (DRY)
function extractProdutosFromPedidos(pedidosSelecionados: Pedido[]): Produto[] {
  return pedidosSelecionados.flatMap((pedido) =>
    (pedido.produtos || []).map((produto) => ({
      codProduto: produto.codProduto || "",
      descricao: produto.descricao || "",
      quantidade: produto.quantidade,
      valorTotal: produto.valorTotal,
      origemProduto: produto.origemProduto,
      ncmProduto: produto.ncmProduto,
      unidadeMedida: produto.unidadeMedida,
      grupoTributario: produto.grupoTributario,
      codProdForn: produto.codProdForn,
      itemPedido: produto.itemPedido,
      pedidoCompra: pedido.codPedido,
      origem: produto.origem as OrigemProduto,
      idUnico: produto.idUnico || "",
      centroCusto: produto.centroCusto,
      armazemPadrao: produto.armazemPadrao,
      grupoProduto: produto.grupoProduto,
      tipoProduto: produto.tipoProduto,
      temRateio: produto.temRateio,
      // Rateios do pedido de compra (SCH010)
      rateiosOC: (produto.rateios || []).map((r) => ({
        filPedido: r.filPedido || "",
        codPedido: r.codPedido || "",
        itemPedido: r.itemPedido || "",
        itemRateio: r.itemRateio || "",
        fornecedor: r.fornecedor || "",
        loja: r.loja || "",
        percentual: r.percentual || 0,
        codCentroCusto: r.codCentroCusto || "",
        valorRateio: r.valorRateio || 0,
        descCentroCusto: r.descCentroCusto || "",
      })),
    }))
  );
}

interface UsePedidoDialogCardReturn {
  open: boolean;
  filter: string;
  selected: string[]; // Agora contém chaves únicas (numero+filial)
  lista: Pedido[];
  fornecedor: { codigo: string; loja: string };
  isLoading: boolean;
  error: Error | null;
  canSelectPedidos: boolean;
  buttonLabel: string;
  setOpen: (open: boolean) => void;
  setFilter: (filter: string) => void;
  toggle: (pedido: Pedido) => void;
  limpar: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  executeConnectionOnClose: () => void;
  // Busca por pré solicitação
  preSolicitacao: string;
  setPreSolicitacao: (v: string) => void;
  isSearchingPreSol: boolean;
  preSolResult: { found: boolean; pedidos: string[] } | null;
  buscarPorPreSolicitacao: (numero: string) => Promise<void>;
}

export function usePedidoDialog(): UsePedidoDialogCardReturn {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [isProcessingConnection, setIsProcessingConnection] = useState(false);
  const [hasRestoredInitialSelection, setHasRestoredInitialSelection] =
    useState(false);

  const { draft, actions, aux } = usePrenotaStore();
  const fornecedor = {
    codigo: draft.documento.fornecedor,
    loja: draft.documento.loja,
  };

  // Busca TanStack
  const {
    searchPedidos,
    isLoading,
    error,
    data: pedidosData,
    clearCache,
  } = useSearchPedidos_tanstack();

  const lastFetch = useRef<{ cod?: string; loja?: string } | null>(null);

  // -------------------------------------- observa mudanças na store
  useEffect(() => {
    const unsubscribe = usePrenotaStore.subscribe((state) => {
      const fornecedor = state.draft.documento.fornecedor;
      const loja = state.draft.documento.loja;

      // Se o fornecedor ou loja foram limpos (reset), limpa o estado interno
      if (!fornecedor || !loja) {
        // Usa setTimeout para evitar erro de atualização durante renderização
        setTimeout(() => {
          setSelected([]);
          setFilter("");
          setOpen(false);
          // Limpa o lastFetch para permitir nova busca
          lastFetch.current = null;
          // Limpa o cache do TanStack Query
          clearCache();
        }, 0);
      }
    });

    return unsubscribe;
  }, [clearCache]);

  // -------------------------------------- restaura seleção inicial (F5) sem limpar estado auxiliar
  useEffect(() => {
    if (hasRestoredInitialSelection) return;
    if (!pedidosData || pedidosData.length === 0) return;

    const availableKeys = new Set(pedidosData.map(getPedidoKey));
    let restoredKeys: string[] = [];

    // Prioridade 1: pedidos já salvos no auxiliar
    if (aux.pedidos && aux.pedidos.length > 0) {
      restoredKeys = aux.pedidos
        .map((pedido) => getPedidoKey(pedido))
        .filter((key) => availableKeys.has(key));
    }

    // Prioridade 2: fallback visual pelos pedidos já vinculados aos produtos do draft
    if (restoredKeys.length === 0) {
      const codigosPedidoNoDraft = Array.from(
        new Set(
          (draft.produtos || [])
            .map((produto) => (produto.pedidoCompra || "").trim())
            .filter(Boolean)
        )
      );
      if (codigosPedidoNoDraft.length > 0) {
        restoredKeys = pedidosData
          .filter((pedido) =>
            codigosPedidoNoDraft.includes((pedido.codPedido || "").trim())
          )
          .map(getPedidoKey);
      }
    }

    if (restoredKeys.length > 0) {
      setSelected((prevSelected) => {
        const prevSet = new Set(prevSelected);
        if (
          prevSet.size === restoredKeys.length &&
          restoredKeys.every((key) => prevSet.has(key))
        ) {
          return prevSelected;
        }
        return restoredKeys;
      });
    }

    setHasRestoredInitialSelection(true);
  }, [hasRestoredInitialSelection, pedidosData, aux.pedidos, draft.produtos]);

  // -------------------------------------- sincroniza estado local com estado persistido
  useEffect(() => {
    if (!hasRestoredInitialSelection) return;

    // Sincroniza o estado local 'selected' com os pedidos persistidos na store
    if (
      aux.pedidos &&
      aux.pedidos.length > 0 &&
      pedidosData &&
      pedidosData.length > 0
    ) {
      const pedidosPersistidos = aux.pedidos;
      const chavesPersistidas = pedidosPersistidos.map((pedido) =>
        getPedidoKey(pedido)
      );

      // Atualiza o estado local apenas se for diferente
      setSelected((prevSelected) => {
        const currentSet = new Set(prevSelected);
        const persistSet = new Set(chavesPersistidas);

        // Se os conjuntos são diferentes, atualiza
        if (
          currentSet.size !== persistSet.size ||
          !chavesPersistidas.every((key) => currentSet.has(key))
        ) {
          return chavesPersistidas;
        }

        return prevSelected;
      });
    }
  }, [aux.pedidos, pedidosData, hasRestoredInitialSelection]);

  // Buscar pedidos ao mudar fornecedor/loja
  useEffect(() => {
    const cod = fornecedor.codigo?.trim();
    const loja = fornecedor.loja?.trim();
    if (
      cod &&
      loja &&
      (lastFetch.current?.cod !== cod || lastFetch.current?.loja !== loja)
    ) {
      lastFetch.current = { cod, loja };
      searchPedidos(cod, loja);
    }
  }, [fornecedor.codigo, fornecedor.loja, searchPedidos]);

  // Lista formatada para combobox com filtro otimizado
  const lista: Pedido[] = useMemo(() => {
    if (!pedidosData?.length) return [];

    // Aplica filtro se houver termo de busca
    if (filter.trim()) {
      // Divide o input em múltiplos pedidos
      const pedidosDigitados = filter
        .toLowerCase()
        .trim()
        .split(/[,;\s]+/)
        .filter(Boolean);

      // Se tiver múltiplos pedidos, busca exata por número
      if (pedidosDigitados.length > 1) {
        return pedidosData.filter((item) =>
          pedidosDigitados.some(
            (pedido) => item.codPedido.toLowerCase() === pedido
          )
        );
      }

      // Se for busca simples, mantém o comportamento original
      const searchTerm = filter.toLowerCase().trim();
      return pedidosData.filter(
        (item) =>
          item.codPedido.toLowerCase().includes(searchTerm) ||
          item.status.toLowerCase().includes(searchTerm) ||
          item.filPedido.toLowerCase().includes(searchTerm) ||
          item.filEntrega.toLowerCase().includes(searchTerm) ||
          (item.observacao &&
            item.observacao.toLowerCase().includes(searchTerm))
      );
    }

    return pedidosData;
  }, [pedidosData, filter]);

  // Seleção otimizada com Set para performance
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Toggle de seleção
  const toggle = useCallback((pedido: Pedido) => {
    const key = getPedidoKey(pedido);
    setSelected((prev) => {
      const newSelected = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key];
      return newSelected;
    });
  }, []);

  // Efeito para sincronizar seleção com store (separado da renderização)
  useEffect(() => {
    // Evita limpar pedidos durante bootstrap/hidratação inicial
    if (!hasRestoredInitialSelection) return;

    const basePedidos = pedidosData || [];
    // Filtra os pedidos selecionados (vazio quando selected.length === 0)
    const pedidosSelecionados = basePedidos.filter((p) =>
      selected.includes(getPedidoKey(p))
    );

    // Quando não há seleção: limpa pedidos e produtos na store para o botão Salvar reagir
    if (selected.length === 0) {
      if (actions.aux?.pedidos?.set) actions.aux.pedidos.set([]);
      if (actions.aux?.produtoProtheus?.set) actions.aux.produtoProtheus.set([]);
      if (actions.aux?.setTotalProdutosPedidos) actions.aux.setTotalProdutosPedidos(0);
      return;
    }

    // Ainda aguardando dados da API para reconciliar seleção restaurada
    if (pedidosSelecionados.length === 0) return;

    // ===== SALVA CONDIÇÃO DE PAGAMENTO =====
    // Pega a condição do primeiro pedido selecionado (ou do único pedido)
    const primeiroPedido = pedidosSelecionados[0];
    if (primeiroPedido?.condicao) {
      // Pega o estado atual dos pagamentos para preservar dados existentes
      const pagamentosAtuais = usePrenotaStore.getState().draft.pagamentos;

      // Salva a condição de pagamento na store, preservando outros dados
      actions.draft.setPagamentos({
        condicaoFinanceira: primeiroPedido.condicao,
        cnpjPix: pagamentosAtuais.cnpjPix || "",
        chavePix: pagamentosAtuais.chavePix || "",
        parcelas: pagamentosAtuais.parcelas || [],
      });
    }

    // Atualiza a store auxiliar - apenas detalhes do pedido (sem produtos)
    if (actions.aux?.pedidos?.set) {
      const pedidosSemProdutos = pedidosSelecionados.map((pedido) => ({
        ...pedido,
        produtos: [], // Array vazio em vez de undefined
      }));
      actions.aux.pedidos.set(pedidosSemProdutos);
    }

    // ===== EXTRAI TODOS OS PRODUTOS DE CADA PEDIDO =====
    const produtosDosPedidos = extractProdutosFromPedidos(pedidosSelecionados);

    // Salva produtos na store produtoProtheus (sem conexão automática ainda)
    if (actions.aux?.produtoProtheus?.set) {
      actions.aux.produtoProtheus.set(produtosDosPedidos);
    }

    // Salva o total de produtos dos pedidos selecionados
    if (actions.aux?.setTotalProdutosPedidos) {
      actions.aux.setTotalProdutosPedidos(produtosDosPedidos.length);
    }
  }, [
    hasRestoredInitialSelection,
    selected,
    pedidosData,
    actions.aux,
    actions.draft,
    actions.aux?.pedidos,
    actions.aux?.produtoProtheus,
  ]);

  // Função para executar conexão automática quando o dialog for fechado
  const executeConnectionOnClose = useCallback(() => {
    if (selected.length === 0) return;

    const basePedidos = pedidosData || [];
    const pedidosSelecionados = basePedidos.filter((p) =>
      selected.includes(getPedidoKey(p))
    );
    if (pedidosSelecionados.length === 0) return;

    const produtosDosPedidos = extractProdutosFromPedidos(pedidosSelecionados);

    // ===== CONEXÃO AUTOMÁTICA NO FECHAMENTO =====
    const xmlItems =
      aux.produtoTabela?.filter(
        (item: any) =>
          item.origem === OrigemProduto.XML || item.origem === "xml"
      ) || [];

    if (
      xmlItems.length > 0 &&
      produtosDosPedidos.length > 0 &&
      !isProcessingConnection
    ) {
      setIsProcessingConnection(true);

      // Usa setTimeout para evitar conflitos de renderização
      setTimeout(() => {
        try {
          const resultado = connectXmlWithPedido({
            xmlItems,
            pedidoItems: produtosDosPedidos,
            tabelaAtual: aux.produtoTabela || [],
            tipoNota: draft.opcoes?.tipoNota,
            onUpdateTabela: (novaTabela: Produto[]) => {
              if (actions.aux?.produtoTabela?.set) {
                actions.aux.produtoTabela.set(novaTabela);
              }
            },
            onUpdateProdutoProtheus: (produtosNaoConectados: Produto[]) => {
              if (actions.aux?.produtoProtheus?.addOrUpdate) {
                produtosNaoConectados.forEach((produto) => {
                  // ✅ VALIDAÇÃO: Só adiciona produtos com origem "pedido"
                  if (produto.origem === "pedido") {
                    actions.aux.produtoProtheus.addOrUpdate(produto);
                  } else {
                    console.warn(`⚠️ [onUpdateProdutoProtheus] Produto ignorado - origem não é pedido:`, {
                      codProduto: produto.codProduto,
                      origem: produto.origem
                    });
                  }
                });
              }
            },
            onClearProdutoProtheus: () => {
              if (actions.aux?.produtoProtheus?.clear) {
                actions.aux.produtoProtheus.clear();
              }
            },
          });
        } catch (error) {
        } finally {
          setIsProcessingConnection(false);
        }
      }, 100);
    }
  }, [
    selected,
    pedidosData,
    aux.produtoTabela,
    actions.aux?.produtoTabela,
    actions.aux?.produtoProtheus,
    draft.opcoes?.tipoNota,
    isProcessingConnection,
  ]);

  // Limpar seleção
  const limpar = useCallback(() => {
    setSelected([]);

    // Limpa o slice de pedidos
    if (actions.aux?.pedidos?.clear) {
      actions.aux.pedidos.clear();
    }

    // Limpa o slice de produtos protheus
    if (actions.aux?.produtoProtheus?.clear) {
      actions.aux.produtoProtheus.clear();
    }

    // Limpa o cache do TanStack Query
    clearCache();
  }, [actions, clearCache]);

  // Teclado (Enter)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter" || isLoading || lista.length === 0) return;
      const trimmedFilter = filter.trim();
      if (!trimmedFilter) return;

      // Busca otimizada
      const exactMatch = lista.find(
        (p) => p.codPedido.toLowerCase() === trimmedFilter.toLowerCase()
      );
      if (exactMatch) {
        toggle(exactMatch);
        return;
      }
      if (lista.length === 1) {
        toggle(lista[0]);
      }
    },
    [filter, isLoading, lista, toggle]
  );

  useEffect(() => {
    if (!open) {
      setFilter("");
      setPreSolicitacao("");
      setPreSolResult(null);
    }
  }, [open]);

  // ===== PRÉ SOLICITAÇÃO =====
  const [preSolicitacao, setPreSolicitacao] = useState("");
  const [isSearchingPreSol, setIsSearchingPreSol] = useState(false);
  const [preSolResult, setPreSolResult] = useState<{
    found: boolean;
    pedidos: string[];
  } | null>(null);

  const buscarPorPreSolicitacao = useCallback(
    async (numero: string) => {
      const num = numero.trim();
      if (!num) return;

      setIsSearchingPreSol(true);
      setPreSolResult(null);

      try {
        const { data } = await apiClient.internal.get<{
          success: boolean;
          data?: string[];
          error?: string;
        }>(URL.in.API_PRENOTAS_PRE_SOLICITACAO, {
          headers: {
            preSolicitacao: num,
            fornecedor: fornecedor.codigo?.trim() || "",
          },
        });

        if (data?.success && data.data && data.data.length > 0) {
          const pedidosEncontrados = data.data;
          setPreSolResult({ found: true, pedidos: pedidosEncontrados });
          // Seta o filter com os códigos dos pedidos encontrados para filtrar a lista
          setFilter(pedidosEncontrados.join(" "));
        } else {
          setPreSolResult({ found: false, pedidos: [] });
        }
      } catch (err) {
        console.error("❌ [PreSolicitacao] Erro na busca:", err);
        setPreSolResult({ found: false, pedidos: [] });
      } finally {
        setIsSearchingPreSol(false);
      }
    },
    [setFilter, fornecedor.codigo]
  );

  // ✅ USA UTILS UNIFICADO: Validação de seleção de pedido
  const canSelectPedidos = identificacaoUtils.pedido.canSelectPedido(
    isLoading,
    error,
    lista.length,
    draft.opcoes?.tipoNota,
    fornecedor
  );

  const buttonLabel = useMemo(() => {
    if (isLoading) return "Carregando...";
    if (error) return "Erro ao carregar";

    // ✅ Sequência de validação: tipo → fornecedor → pedido
    if (!draft.opcoes?.tipoNota) return "Selecione o tipo primeiro";
    if (!fornecedor.codigo || !fornecedor.loja)
      return "Selecione o fornecedor primeiro";

    const sourceForLabel = pedidosData || lista;
    if (sourceForLabel.length === 0) return "Nenhum pedido";
    if (selected.length > 0) {
      const pedidosSelecionados = sourceForLabel
        .filter((p) => selectedSet.has(getPedidoKey(p)))
        .map((p) => `${p.codPedido} (${p.filPedido})`);
      if (pedidosSelecionados.length === 0) {
        return "Restaurando pedidos...";
      }
      return `${pedidosSelecionados.join(", ")}`;
    }
    return "Selecionar Pedido";
  }, [
    isLoading,
    error,
    selected,
    selectedSet,
    pedidosData,
    lista,
    draft.opcoes?.tipoNota,
    fornecedor.codigo,
    fornecedor.loja,
  ]);

  return {
    open,
    filter,
    selected,
    lista,
    fornecedor,
    isLoading,
    error,
    canSelectPedidos,
    buttonLabel,
    setOpen,
    setFilter,
    toggle,
    limpar,
    handleKeyDown,
    executeConnectionOnClose,
    // Pré solicitação
    preSolicitacao,
    setPreSolicitacao,
    isSearchingPreSol,
    preSolResult,
    buscarPorPreSolicitacao,
  };
}
